import CloseIcon from "@assets/images/CloseModal.svg";
import SendMailIcon from "@assets/images/send-mail.svg";
import SendMailOkIcon from "@assets/images/sent-mail-success.svg";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { SingleTextField } from "@saleor/components/SingleTextField";
import TextEditor from "@saleor/components/TextEditor";
import TextInputDisable from "@saleor/components/TextInputDisable";
import {
  DomesticOrderConfirmationFilterInput,
  ListOrderConfirmationSapQuery,
  useGetOrderEmailToAndCcQuery,
  useSendOrderEmailMutation
} from "@saleor/graphql";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNotifier from "@saleor/hooks/useNotifier";
import { OrderReportLanguageEnum } from "@saleor/OrderConfirm/const";
import classNames from "classnames";
import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "./styles";

interface SoldToItem {
  soldToCode: string;
  soldToName: string;
  orderIds: string[];
  orderLineIds: string[];
  saleOrg: string;
  contractNo: string[];
}

export interface SendMailProps {
  dataSearchList: ListOrderConfirmationSapQuery["listOrderConfirmationSap"];
  soldToList: SoldToItem[];
  open: boolean;
  filter: DomesticOrderConfirmationFilterInput;
  onClose: () => void;
  reportLanguage?: OrderReportLanguageEnum;
}

const ModalSendMailMultiple: React.FC<SendMailProps> = props => {
  const initSoldTo = {
    soldToCode: "",
    soldToName: "",
    orderIds: [],
    orderLineIds: [],
    saleOrg: "",
    contractNo: []
  };
  const {
    open,
    onClose,
    soldToList: soldToListInit,
    dataSearchList,
    filter,
    reportLanguage = OrderReportLanguageEnum.TH
  } = props;
  const {
    listElements: listSendMail,
    add: addSendMail,
    reset: clearListSendMail
  } = useBulkActions();
  const [soldTo, setSoldTo] = useState<SoldToItem>(initSoldTo);
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);
  const classes = useStyles();
  const intl = useIntl();
  const notify = useNotifier();

  const SendMailSchema = Yup.object().shape({
    to: Yup.string().required("Field To is required"),
    cc: Yup.string().required("Field Cc is required"),
    content: Yup.string()
      .test("check-content-empty", function(content) {
        return (
          new DOMParser()
            .parseFromString(content, "text/html")
            .body.textContent.trim().length > 0
        );
      })
      .required("Field Content is required")
  });
  const { data: dataMail, loading } = useGetOrderEmailToAndCcQuery({
    variables: {
      bu: filter?.bu,
      soldToCode: [soldTo.soldToCode],
      saleOrg: soldTo.saleOrg,
      soNo: soldTo.orderIds
    },
    skip: !soldTo.soldToCode || !open
  });

  useEffect(() => {
    if (soldToListInit.length > 0 && listSendMail.length === 0 && open) {
      setSoldTo(soldToListInit[0]);
    } else {
      setSoldTo(initSoldTo);
      clearListSendMail();
    }
  }, [soldToListInit, open]);

  const [sendMailOrder] = useSendOrderEmailMutation({
    onCompleted: data => {
      setOpenSuccess(true);
      if (
        data?.sendOrderEmail?.errors.length === 0 &&
        soldToListInit.length === listSendMail.length
      ) {
        clearListSendMail();
        onClose();
      }
    },
    onError: data => {
      notify({
        status: "error",
        apiMessage: data.message
      });
      if (soldToListInit.length === listSendMail.length) {
        clearListSendMail();
        onClose();
      }
    }
  });

  const formik = useFormik({
    initialValues: {
      to: dataMail?.getOrderEmailToAndCc?.to,
      cc: dataMail?.getOrderEmailToAndCc?.cc,
      subject:
        reportLanguage === OrderReportLanguageEnum.CN
          ? `Order Confirmation 订购确认 ${soldTo.soldToName} ${moment().format(
              "DD/MM/YYYY"
            )}`
          : `Order Confirmation ${soldTo.soldToName} ${moment().format(
              "DD/MM/YYYY"
            )}`,
      content:
        reportLanguage === OrderReportLanguageEnum.CN
          ? "<p>Dear Sir, 尊敬的客户：</p>" +
            "<p>Please see Order Confirmation as per attachment. 附件为订单确认，请查收。</p>" +
            "<p></p>" +
            "<p>Regards, 感谢您的支持！</p>" +
            "<p>Customer Services 客户服务部</p>" +
            "<p>SCGP</p>"
          : "<p>Dear Sir,</p>" +
            "<p>Please see Order Confirmation as per attachment.</p>" +
            "<p></p>" +
            "<p>Regards,</p>" +
            "<p>Customer Services</p>" +
            "<p>SCGP</p>"
    },
    enableReinitialize: true,
    onSubmit: () => {
      addSendMail(soldTo.soldToCode);
      const findSoldToSendMail = soldToListInit.find(
        item =>
          !listSendMail.includes(item.soldToCode) &&
          item.soldToCode !== soldTo.soldToCode
      );
      if (findSoldToSendMail) {
        setSoldTo(findSoldToSendMail);
      }
      const dataOrderListMapping = dataSearchList
        .filter(order => soldTo.orderIds.includes(order.soNo))
        .map(order => {
          const orderLines = order.orderLines
            .filter(line =>
              soldTo.orderLineIds.includes(`${order.soNo}_${line.itemNo}`)
            )
            .map(line => ({
              itemNo: line.itemNo,
              remark: line.remarkOrder,
              materialDescription: line.materialDescription,
              orderQty: line.orderQty.toString(),
              nonConfirmQty: line.nonConfirmQuantity.toString(),
              confirmQty: line.confirmQuantity.toString(),
              saleUnit: line.saleUnit,
              firstDeliveryDate: line.requestDate,
              soldTo: line.soldToCode,
              status: line.status,
              iplanConfirmDate: line.confirmDate,
              materialCode: line.materialCode
            }));
          return {
            soNo: order.soNo,
            contractNo: order.contractNo,
            createdDate: order.orderDate,
            poNo: order.poNo,
            paymentTermDesc: order.paymentMethodName,
            contractName: order.contractName,
            itemNo: ["10"],
            shipTo: order.shipToCode,
            saleOrg: order.orderLines?.[0].saleOrg,
            isNotRef: order.isNotRef,
            orderLines
          };
        });
      const data = {
        ...formik.values,
        orders: dataOrderListMapping,
        reportLanguage
      };
      sendMailOrder({
        variables: data
      });
    },
    validationSchema: SendMailSchema
  });

  const handleOnClose = () => {
    onClose();
    formik.resetForm();
    clearListSendMail();
  };

  return (
    <>
      <Dialog
        className={classes.root}
        open={open}
        onClose={handleOnClose}
        key={soldTo.soldToCode}
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailIcon} className={classes.icon} />
            <MyTypography variant={"h5"}>
              {intl.formatMessage({ defaultMessage: "Send Email" })}
            </MyTypography>
          </div>
          <SVG
            data-testid="close-icon"
            onClick={handleOnClose}
            width={24}
            height={24}
            src={CloseIcon}
            className={classes.closeIcon}
          />
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid
            spacing={3}
            container
            alignItems={"center"}
            className={classes.gridBox}
          >
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "To" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name={"to"}
                testId={"to"}
                value={formik?.values?.to || ""}
                onChange={formik.handleChange}
                error={!!formik.errors.to && !!formik.touched.to}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Cc" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name={"cc"}
                testId={"cc"}
                value={formik?.values?.cc || ""}
                onChange={formik.handleChange}
                error={!!formik.errors.cc && !!formik.touched.cc}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Subject" })}:
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <TextInputDisable
                testId={"subject"}
                value={formik.values.subject}
              />
            </Grid>
            <Grid sm={12} item>
              <div
                className={classNames(
                  classes.contentBlock,
                  formik.errors.content &&
                    formik.touched.content &&
                    classes.errorBlockContent
                )}
              >
                <MyTypography variant="body3" className={classes.contentTitle}>
                  {intl.formatMessage({ defaultMessage: "Message" })}
                  <span className={classes.required}>*</span>
                </MyTypography>
                <div className={classes.contentEditor}>
                  <TextEditor
                    name={"content"}
                    testId={"content"}
                    value={formik.values.content}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>
            </Grid>
            <Grid sm={12} item className={classes.flexEnd}>
              <Button
                disabled={
                  loading || listSendMail.length === soldToListInit.length
                }
                type={"submit"}
                color="secondary"
                variant="contained"
                onClick={() => {
                  formik.submitForm();
                }}
              >
                <MyTypography variant="body1">
                  {intl.formatMessage({ defaultMessage: "Send" })}
                </MyTypography>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog data-testid="dialog-success" open={openSuccess}>
        <DialogContent className={classes.boxSuccess}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailOkIcon} className={classes.icon} />
            <MyTypography variant={"h6"}>
              {intl.formatMessage({ defaultMessage: "ส่งอีเมล สำเร็จ" })}
            </MyTypography>
          </div>
          <MyTypography variant={"body2"}>
            {intl.formatMessage({
              defaultMessage: "ระบบ eOrdering ได้ส่งอีเมลของคุณเรียบร้อยแล้ว"
            })}
          </MyTypography>
          <Button
            fullWidth
            color="secondary"
            variant="contained"
            onClick={() => {
              setOpenSuccess(false);
            }}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
            </MyTypography>
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalSendMailMultiple;
